import { put, take, call, select } from 'redux-saga/effects';
import * as SagaKit from 'redux-saga-kit';
import { actions, types } from '../actions/FormActions';
import { types as sessionTypes } from '../actions/SessionActions';
import { types as organizationTypes } from '@wix/restaurants-redux/dist/actions/OrganizationActions';
import { printTime, getMaxDelay, getFormOptions } from '../shared/utils/ReservationHelpers';
import _ from 'lodash';

// ============================================================================
// SAGAS
// ============================================================================

const initFormOptions = ({ReservationUtils}) => function* ({payload}) {
    const { organization } = payload;
    const i18n = yield select(i18nSelector);
    yield put(actions.setFormOptions(getFormOptions(ReservationUtils, organization, i18n.localeData)));
};

const initFormOptionsFromState = ({ReservationUtils}) => function* () {
    const organization = yield select(restaurantSelector);
    const i18n = yield select(i18nSelector);
    yield put(actions.setFormOptions(getFormOptions(ReservationUtils, organization, i18n.localeData)));
};

const updateField = ({moment, ReservationUtils}) => function* ({payload}) {
    const { type, value } = payload;
    const restaurant = yield select(restaurantSelector);

    switch (type) {
        case 'date':
            yield call(validateTimeAndDate({ReservationUtils, moment}), restaurant, value);
            yield call(setTimeOptions({ReservationUtils, moment}), restaurant, value);
            break;
    }

    yield put(actions.updateFieldEnd(payload));
};

const validateReservation = ({ReservationValidator}) => function* () {
    let errors = yield call(checkForReservationErrors({ReservationValidator}));

    while (!_.isEmpty(errors)) {
        yield put(actions.reservationValidationError({errors}));
        const { payload } = yield take(types.UPDATE_FIELD_END);
        errors = yield call(checkForReservationErrors({ReservationValidator}), payload);
    }
    yield put(actions.reservationValidationOk());
};

const validateFullReservation = ({ReservationValidator}) => function* () {
    let errors = yield call(checkForReservationErrors({ReservationValidator}), {}, true);

    while (!_.isEmpty(errors)) {

        yield put(actions.fullReservationValidationError({errors}));
        yield put(actions.setShowPolicyError({showPolicyError:false})) // policy error should not be shown if there are other errors

        const { payload } = yield take(types.UPDATE_FIELD);
        errors = yield call(checkForReservationErrors({ReservationValidator}), payload, true);
    }
    const policyError=(yield select(errorsSelector)).policyError
    if(!policyError){
        yield put(actions.fullReservationValidationOk());

    }
    else {
        yield put(actions.setShowPolicyError({showPolicyError:true}))
        yield put(actions.fullReservationValidationError({errors:[{type:
                'showPolicyError',error:true},{type:'policyError',error:true}]}));

    }

};

const resetForm = () => function* () {
    yield put(actions.resetForm());
};

// ============================================================================
// HELPERS
// ============================================================================

const checkForReservationErrors = ({ReservationValidator}) => function* (payload = {}, fullReservations = false) {
    const { type, value } = payload;
    const restaurant = yield select(restaurantSelector);
    const reservation = yield select(reservationSelector);
    const maxDelay = getMaxDelay(reservation);
    if (!restaurant.reservations) restaurant.reservations = {};
    restaurant.reservations.futureDelayMins = {...restaurant.reservations.futureDelayMins, max: maxDelay};
    const validator = new ReservationValidator(restaurant);
    return validator.validate({...reservation, ...{[type]: value}}, fullReservations);
};

const getTimeOptions = ({ReservationUtils, moment}) => function* (restaurant, value) {
    value = value ? moment.tz(value, restaurant.timezone) : null;
    let timeOptions = ReservationUtils.getReservationHourOption(restaurant, value);
    timeOptions = yield call(adjustTimeOptions({moment}), timeOptions);
    return timeOptions;
};

const setTimeOptions = ({ReservationUtils, moment}) => function* (restaurant, value) {
    const timeOptions = yield call(getTimeOptions({ReservationUtils, moment}), restaurant, value);
    yield put(actions.setFormOptions({timeOptions}));
};

const validateTimeAndDate = ({ReservationUtils, moment}) => function* (restaurant, value) {
    const { reservations } = restaurant;
    const selectedReservationDate = moment.tz(value, restaurant.timezone);
    const time = yield select(timeSelector);
    if (!_.isEmpty(time)) {
        selectedReservationDate.set('hour', time.hour).set('minute', time.minutes);
        if (reservations && reservations.availability) {
            const isValid = ReservationUtils.isValidReservation(restaurant, selectedReservationDate);
            if (isValid !== true) {
                yield put(actions.updateField({value: null, type: 'time'}));
            }
        }
    }
};

const adjustTimeOptions = ({moment}) => function (timeOptions) {
    return _.map(timeOptions, option => {
        const time = moment().set('minute', option.minutes).set('hour', option.hour);
        return {
            value: option,
            name: printTime(time),
            type: 'time'
        };
    });
};

// ============================================================================
// SELECTORS
// ============================================================================

const reservationSelector = SagaKit.stateSelector('seatingForm.reservation');
const timeSelector = SagaKit.stateSelector('seatingForm.reservation.time');
const restaurantSelector = SagaKit.stateSelector('organization.organization', {});
const errorsSelector = SagaKit.stateSelector('seatingForm.errors', {});
const i18nSelector = SagaKit.stateSelector('i18nData', {});

// ============================================================================
// EXPORT
// ============================================================================

export default SagaKit.sagaCreator({
    [organizationTypes.ORGANIZATION_LOAD]: {handler: initFormOptions, takeLatest: true},
    [sessionTypes.INIT_SESSION]: {handler: initFormOptionsFromState, takeLatest: true},
    [types.UPDATE_FIELD] : {handler: updateField, takeLatest: false},
    [types.VALIDATE_RESERVATION] : {handler: validateReservation, takeLatest: true, cancelOn: [sessionTypes.SHOW_DETAILS_FORM]},
    [types.VALIDATE_FULL_RESERVATION] : {handler: validateFullReservation, takeLatest: true, cancelOn: [sessionTypes.submitFullReservationForm, sessionTypes.FORM_END]},
    [sessionTypes.RESERVATION_SUCCESS] : {handler: resetForm, takeLatest: true}
});
